// Role Definitions and Permissions for AAR Guest Management System

export const ROLES = {
  SYS_ADMIN: {
    id: 'sys_admin',
    name: 'System Administrator',
    permissions: ['all'], // Wildcard permission - full access
    color: '#0d4d7a',
    description: 'Full system access including user management, admin panel, and all features'
  },
  EXECUTIVE: {
    id: 'executive',
    name: 'Executive',
    permissions: [
      'dashboard:read'
    ],
    color: '#1a7a9a',
    description: 'View-only access to dashboard - can see guest check-in/out activity'
  },
  AAR_LEADER: {
    id: 'aar_leader',
    name: 'AAR Leader',
    permissions: [
      'checkin:read',
      'checkin:write',
      'checkout:read',
      'checkout:write',
      'dashboard:read',
      'profiles:read',
      'profiles:write'
    ],
    color: '#2ba7c9',
    description: 'Can check guests in/out, view dashboard, and manage guest/volunteer profiles'
  },
  BT_ADMIN: {
    id: 'bt_admin',
    name: 'BT Admin',
    permissions: [
      'checkin:read',
      'checkin:write',
      'checkout:read',
      'checkout:write',
      'dashboard:read',
      'profiles:read',
      'profiles:write',
      'reports:read',
      'reports:generate'
    ],
    color: '#185a86',
    description: 'AAR Leader access plus Reports menu (Guest Activities, Timeline, Pager Report)'
  },
  AAR_VOLUNTEER: {
    id: 'aar_volunteer',
    name: 'AAR Volunteer',
    permissions: [
      'checkin:read',
      'checkin:write',
      'checkout:read',
      'checkout:write',
      'dashboard:read'
    ],
    color: '#3db8d9',
    description: 'Can check guests in/out and view dashboard - NO reports or admin access'
  }
};

// Default role for new users
export const DEFAULT_ROLE = 'aar_volunteer';

// Admin email(s) - users who always have sys_admin role
export const ADMIN_EMAILS = [
  'rsalomon@brooklyntab.org',
  'ron.salomon@hotmail.com'
];

// CCB group IDs that drive the app's directories
export const GUEST_GROUP_ID = '1269';      // AAR guests
export const VOLUNTEER_GROUP_ID = '715';   // AAR volunteers

// Locations for attendance tracking
export const ATTENDANCE_LOCATIONS = [
  { id: '163_livingston', name: '163 Livingston' },
  { id: 'smith_lobby', name: 'Smith Lobby' }
];

// Service times
export const SERVICE_TYPES = {
  SUNDAY_9AM: {
    id: '9am',
    name: 'Sunday 9:00 AM Service',
    day: 0, // Sunday
    time: '09:00'
  },
  SUNDAY_12PM: {
    id: '12pm',
    name: 'Sunday 12:00 PM Service',
    day: 0, // Sunday
    time: '12:00'
  },
  SUNDAY_3PM: {
    id: '3pm',
    name: 'Sunday 3:00 PM Service',
    day: 0, // Sunday
    time: '15:00'
  },
  TUESDAY_PRAYER: {
    id: 'tuesday_prayer',
    name: 'Tuesday 6:30 PM Prayer Meeting',
    day: 2, // Tuesday
    time: '18:30'
  }
};

/**
 * Check if a user has a specific permission
 * @param {string} userRole - User's role ID
 * @param {string} permission - Permission to check (e.g., 'guests:write')
 * @returns {boolean}
 */
export const hasPermission = (userRole, permission) => {
  if (!userRole) return false;

  // Find role object
  const role = Object.values(ROLES).find(r => r.id === userRole);
  if (!role) return false;

  // System admins have all permissions
  if (role.permissions.includes('all')) return true;

  // Check if permission exists in role's permissions
  return role.permissions.includes(permission);
};

/**
 * Check if user can access admin panel
 * @param {string} userRole
 * @returns {boolean}
 */
export const canAccessAdmin = (userRole) => {
  return userRole === 'sys_admin';
};

/**
 * Check if user can access Guest Profiles page
 * @param {string} userRole
 * @returns {boolean}
 */
export const canAccessGuestProfiles = (userRole) => {
  return userRole === 'sys_admin' || userRole === 'aar_leader' || userRole === 'bt_admin';
};

/**
 * Check if user can access the Reports menu (Guest Activities, Timeline,
 * Pager Report). sys_admin always has access; bt_admin has it as the
 * additional capability above AAR Leader.
 * @param {string} userRole
 * @returns {boolean}
 */
export const canAccessReports = (userRole) => {
  return userRole === 'sys_admin' || userRole === 'bt_admin';
};

/**
 * Check if user can check in/out guests
 * @param {string} userRole
 * @returns {boolean}
 */
export const canCheckInOut = (userRole) => {
  return hasPermission(userRole, 'checkin:write');
};

/**
 * Check if user can generate reports
 * @param {string} userRole
 * @returns {boolean}
 */
export const canGenerateReports = (userRole) => {
  return hasPermission(userRole, 'reports:generate');
};

/**
 * Check if user can manage other users
 * @param {string} userRole
 * @returns {boolean}
 */
export const canManageUsers = (userRole) => {
  return userRole === 'sys_admin';
};

/**
 * Get role display name
 * @param {string} roleId
 * @returns {string}
 */
export const getRoleName = (roleId) => {
  const role = Object.values(ROLES).find(r => r.id === roleId);
  return role ? role.name : 'Unknown Role';
};

/**
 * Get role color
 * @param {string} roleId
 * @returns {string}
 */
export const getRoleColor = (roleId) => {
  const role = Object.values(ROLES).find(r => r.id === roleId);
  return role ? role.color : '#95a5a6';
};

/**
 * Get all available roles
 * @returns {Array}
 */
export const getAllRoles = () => {
  return Object.values(ROLES);
};

/**
 * Get service type by ID
 * @param {string} serviceId
 * @returns {Object|null}
 */
export const getServiceType = (serviceId) => {
  return Object.values(SERVICE_TYPES).find(s => s.id === serviceId) || null;
};

/**
 * Get all service types
 * @returns {Array}
 */
export const getAllServiceTypes = () => {
  return Object.values(SERVICE_TYPES);
};

/**
 * Get service types for a specific day
 * @param {number} dayOfWeek - 0 for Sunday, 2 for Tuesday
 * @returns {Array}
 */
export const getServiceTypesForDay = (dayOfWeek) => {
  return Object.values(SERVICE_TYPES).filter(s => s.day === dayOfWeek);
};

/**
 * Check if email is an admin email
 * @param {string} email
 * @returns {boolean}
 */
export const isAdminEmail = (email) => {
  return ADMIN_EMAILS.includes(email.toLowerCase());
};

/**
 * Normalize legacy role names
 * @param {string} role
 * @returns {string}
 */
export const normalizeRole = (role) => {
  const roleMap = {
    'admin': 'sys_admin',
    'leader': 'aar_leader',
    'volunteer': 'aar_volunteer' // Map old volunteer to aar_volunteer
  };

  return roleMap[role] || role;
};
